import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, imgUrl } from "../lib/storeApi";
import SEO from "../components/SEO";
import { Calendar, ArrowLeft, Phone } from "lucide-react";
import { company } from "../data/mock";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "");

const BlogPost = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setP(null); setNotFound(false);
    fetch(`${API}/blog/posts/${slug}`).then((r) => { if (!r.ok) throw new Error(); return r.json(); }).then(setP).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <main className="max-w-2xl mx-auto px-5 py-24 text-center">
      <SEO title="Post not found" description="This blog post could not be found." path={`/blog/${slug}`} />
      <p className="text-slate-600">That post doesn't exist.</p>
      <Link to="/blog" className="text-[#3b9dff] font-semibold mt-2 inline-block">← Back to blog</Link>
    </main>
  );
  if (!p) return <main className="max-w-3xl mx-auto px-5 py-24 text-slate-400">Loading…</main>;

  const cover = imgUrl(p.cover_image);
  const schema = {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title,
    description: p.meta_description || p.excerpt,
    image: cover ? [cover] : undefined,
    datePublished: p.published_at || p.created_at, dateModified: p.updated_at || p.published_at || p.created_at,
    author: { "@type": "Organization", name: p.author || "Set Up Home Theater" },
    publisher: { "@type": "Organization", name: "Set Up Home Theater", logo: { "@type": "ImageObject", url: company.logo } },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.setuphometheater.com/blog/${p.slug}` },
    keywords: (p.tags || []).join(", ") || undefined,
  };

  return (
    <main data-testid="blog-post">
      <SEO title={p.meta_title || p.title} description={p.meta_description || p.excerpt} path={`/blog/${p.slug}`} image={cover || undefined} jsonLd={schema} />
      <article className="max-w-[760px] mx-auto px-5 lg:px-8 py-12">
        <Link to="/blog" className="text-sm text-slate-400 hover:text-[#3b9dff] inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="w-4 h-4" /> All posts</Link>
        <p className="text-xs text-[#3b9dff] font-semibold flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(p.published_at || p.created_at)}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0a1f44] mt-3 leading-tight">{p.title}</h1>
        {p.excerpt && <p className="text-lg text-slate-500 mt-4">{p.excerpt}</p>}
        {cover && <img src={cover} alt={p.title} className="w-full rounded-2xl mt-8 border border-slate-200" />}
        <div
          data-testid="blog-content"
          className="mt-8 text-slate-700 leading-relaxed [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-[#0a1f44] [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#0a1f44] [&_h3]:mt-8 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1.5 [&_a]:text-[#3b9dff] [&_a]:underline [&_a]:underline-offset-2 [&_img]:rounded-xl [&_img]:my-6 [&_img]:border [&_img]:border-slate-200 [&_blockquote]:border-l-4 [&_blockquote]:border-[#3b9dff] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-500 [&_blockquote]:my-4 [&_strong]:font-bold [&_strong]:text-[#0a1f44] [&_table]:w-full [&_table]:my-4 [&_th]:text-left [&_th]:font-bold [&_th]:p-2 [&_td]:p-2 [&_td]:border-t [&_td]:border-slate-100"
          dangerouslySetInnerHTML={{ __html: p.content_html || "" }}
        />
        {(p.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {p.tags.map((t) => <span key={t} className="text-xs font-semibold text-[#2b8ae8] bg-[#3b9dff]/10 px-3 py-1 rounded-full">#{t}</span>)}
          </div>
        )}
        <div className="mt-12 bg-[#0a1f44] rounded-2xl p-8 text-center text-white">
          <h2 className="text-xl font-extrabold">Ready to upgrade your setup?</h2>
          <p className="text-slate-300 text-sm mt-2">Our local team designs, installs and calibrates it all. Get a free quote today.</p>
          <a href={company.phoneLink} className="inline-flex items-center gap-2 mt-4 bg-[#3b9dff] hover:bg-[#2b8ae8] text-white px-6 py-3 rounded-full font-semibold"><Phone className="w-4 h-4" /> Call {company.phone}</a>
        </div>
      </article>
    </main>
  );
};

export default BlogPost;
